// src/pages/Dashboard/Admin/DepensesRecettesDashboard/hooks/useDepensesRecettesDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createDepenseRecetteClub,
  deleteDepenseRecetteClub,
  getDepensesRecettesClub,
} from "../../../../../services/depensesrecettesclubServices";
import { getAllPlanComptables } from "../../../../../services/plancomptableServices";
import { getAllReglementInscription } from "../../../../../services/reglementinscriptionServices";
import { getSettings } from "../../../../../services/settingsServices";
import { getReglements as getAchatsBoutique } from "../../../../../services/achatsServices";

import {
  buildExportRows,
  calculateAccountingStats,
  calculateAccountingTotals,
  downloadRowsAsCsv,
  downloadRowsAsXlsx,
  filterOperations,
  findBoutiquePlan,
  findCotisationPlan,
  findLicensePlans,
  findPassSportPlans,
  findReductionsPlan,
  getActiveSeasonFromSettings,
  getCategoriesFromOperations,
  getSaisonsFromOperations,
  isAcompteText,
  safeArray,
  seasonBounds,
  todayStr,
  toNumber,
} from "../utils/accountingDashboardUtils";

export function useDepensesRecettesDashboard() {
  const [data, setData] = useState([]);
  const [planComptables, setPlanComptables] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    q: "",
    saison: "",
    categorie: "",
    type: "",
    planComptableId: "",
  });

  const [automationLoading, setAutomationLoading] = useState({
    cotisation: false,
    licences: false,
    passSport: false,
    reductions: false,
    boutique: false,
  });

  const setAutomation = useCallback((key, value) => {
    setAutomationLoading((prev) => ({ ...prev, [key]: value }));
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);

      const [operationsResponse, plansResponse, settingsResponse] =
        await Promise.all([
          getDepensesRecettesClub(),
          getAllPlanComptables(),
          getSettings(),
        ]);

      const operationsRows = safeArray(operationsResponse).sort(
        (a, b) =>
          new Date(b?.date_operation || 0).getTime() -
          new Date(a?.date_operation || 0).getTime()
      );

      const plansRows = safeArray(plansResponse).sort((a, b) => {
        if (a?.sens === b?.sens) {
          return Number(a?.ordre || 0) - Number(b?.ordre || 0);
        }
        return a?.sens === "RECETTE" ? -1 : 1;
      });

      setData(operationsRows);
      setPlanComptables(plansRows);
      setSettings(safeArray(settingsResponse)[0] || settingsResponse || null);

      const activeSeason = getActiveSeasonFromSettings(settingsResponse);

      setFilters((prev) => ({
        ...prev,
        saison: prev.saison || activeSeason || "",
      }));
    } catch (error) {
      console.error("[useDepensesRecettesDashboard] fetchAll error:", error);
      toast.error("Erreur lors du chargement des dépenses et recettes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      q: "",
      saison: getActiveSeasonFromSettings(settings) || "",
      categorie: "",
      type: "",
      planComptableId: "",
    });
  }, [settings]);

  const saisons = useMemo(() => getSaisonsFromOperations(data), [data]);
  const categories = useMemo(() => getCategoriesFromOperations(data), [data]);

  const filtered = useMemo(() => {
    return filterOperations(data, filters);
  }, [data, filters]);

  const totals = useMemo(() => calculateAccountingTotals(filtered), [filtered]);

  const stats = useMemo(() => {
    return calculateAccountingStats(filtered, planComptables);
  }, [filtered, planComptables]);

  const getCurrentSeason = useCallback(async () => {
    if (filters.saison) return filters.saison;

    const active = getActiveSeasonFromSettings(settings);
    if (active) return active;

    const settingsResponse = await getSettings();
    return getActiveSeasonFromSettings(settingsResponse);
  }, [filters.saison, settings]);

  const operationExists = useCallback(
    ({ saison, designation, typeOperation }) => {
      return safeArray(data).some(
        (operation) =>
          String(operation?.saison || "") === String(saison || "") &&
          String(operation?.designation || "") === String(designation || "") &&
          String(operation?.type_operation || "") ===
            String(typeOperation || "")
      );
    },
    [data]
  );

  const createAutomaticOperation = useCallback(
    async (payload) => {
      await createDepenseRecetteClub(payload);
      await fetchAll();
    },
    [fetchAll]
  );

  const addCotisation = useCallback(async () => {
    if (automationLoading.cotisation) return;
    setAutomation("cotisation", true);

    try {
      const season = await getCurrentSeason();

      if (!season) {
        toast.error("Saison introuvable. Sélectionnez une saison.");
        return;
      }

      const regs = safeArray(await getAllReglementInscription());

      const totalCotisation = regs
        .filter(
          (reglement) =>
            String(reglement?.saison || "") === season &&
            String(reglement?.statut_reglement || "") === "Réglé"
        )
        .reduce(
          (sum, reglement) => sum + toNumber(reglement?.montant_cotisation),
          0
        );

      if (totalCotisation <= 0) {
        toast.warn(`Aucune cotisation réglée trouvée pour la saison ${season}.`);
        return;
      }

      const label = `Cotisations perçues — ${season}`;

      if (
        operationExists({
          saison: season,
          designation: label,
          typeOperation: "Recette",
        }) &&
        !window.confirm(`Une ligne "${label}" existe déjà. En créer une autre ?`)
      ) {
        return;
      }

      const plan = findCotisationPlan(planComptables);

      await createAutomaticOperation({
        saison: season,
        date_operation: todayStr(),
        designation: label,
        categorie: "Cotisations",
        beneficiaire: "Club",
        montant: Number(totalCotisation.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: plan?.id ?? null,
      });

      setFilter("saison", season);
      toast.success(`Cotisations ajoutées (${totalCotisation.toFixed(2)} €).`);
    } catch (error) {
      console.error("[addCotisation] error:", error);
      toast.error("Impossible d’ajouter la ligne de cotisation.");
    } finally {
      setAutomation("cotisation", false);
    }
  }, [
    automationLoading.cotisation,
    createAutomaticOperation,
    getCurrentSeason,
    operationExists,
    planComptables,
    setAutomation,
    setFilter,
  ]);

  const addLicences = useCallback(async () => {
    if (automationLoading.licences) return;
    setAutomation("licences", true);

    try {
      const season = await getCurrentSeason();

      if (!season) {
        toast.error("Saison introuvable. Sélectionnez une saison.");
        return;
      }

      const regs = safeArray(await getAllReglementInscription());

      const totalLicences = regs
        .filter(
          (reglement) =>
            String(reglement?.saison || "") === season &&
            String(reglement?.statut_reglement || "") === "Réglé"
        )
        .reduce(
          (sum, reglement) => sum + toNumber(reglement?.montant_lic_assurance),
          0
        );

      if (totalLicences <= 0) {
        toast.warn(`Aucune licence réglée trouvée pour la saison ${season}.`);
        return;
      }

      const labelRecette = `Licences perçues — ${season}`;
      const labelDepense = `Licences reversées FFJDA — ${season}`;

      const exists =
        operationExists({
          saison: season,
          designation: labelRecette,
          typeOperation: "Recette",
        }) ||
        operationExists({
          saison: season,
          designation: labelDepense,
          typeOperation: "Dépense",
        });

      if (
        exists &&
        !window.confirm(
          "Une écriture Licences existe déjà pour cette saison. Créer tout de même les écritures ?"
        )
      ) {
        return;
      }

      const { recettePlan, depensePlan } = findLicensePlans(planComptables);

      await createDepenseRecetteClub({
        saison: season,
        date_operation: todayStr(),
        designation: labelRecette,
        categorie: "Licences",
        beneficiaire: "Club",
        montant: Number(totalLicences.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: recettePlan?.id ?? null,
      });

      await createDepenseRecetteClub({
        saison: season,
        date_operation: todayStr(),
        designation: labelDepense,
        categorie: "Licences",
        beneficiaire: "France Judo",
        montant: Number(totalLicences.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Dépense",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: depensePlan?.id ?? null,
      });

      await fetchAll();

      setFilter("saison", season);
      toast.success(
        `Écritures licences créées (${totalLicences.toFixed(2)} €).`
      );
    } catch (error) {
      console.error("[addLicences] error:", error);
      toast.error("Impossible d’ajouter les écritures de licences.");
    } finally {
      setAutomation("licences", false);
    }
  }, [
    automationLoading.licences,
    fetchAll,
    getCurrentSeason,
    operationExists,
    planComptables,
    setAutomation,
    setFilter,
  ]);

  const addPassSport = useCallback(async () => {
    if (automationLoading.passSport) return;
    setAutomation("passSport", true);

    try {
      const season = await getCurrentSeason();

      if (!season) {
        toast.error("Saison introuvable. Sélectionnez une saison.");
        return;
      }

      const regs = safeArray(await getAllReglementInscription());

      const totalPassSport = regs
        .filter(
          (reglement) =>
            String(reglement?.saison || "") === season &&
            String(reglement?.statut_reglement || "") === "Réglé"
        )
        .reduce((sum, reglement) => {
          const value = toNumber(reglement?.pass_sport);
          return sum + (value < 0 ? Math.abs(value) : 0);
        }, 0);

      if (totalPassSport <= 0) {
        toast.warn(`Aucun Pass’Sport trouvé pour la saison ${season}.`);
        return;
      }

      const labelDepense = `Pass’Sport accordé — ${season}`;
      const labelRecette = `Pass’Sport remboursé (État) — ${season}`;

      const exists =
        operationExists({
          saison: season,
          designation: labelDepense,
          typeOperation: "Dépense",
        }) ||
        operationExists({
          saison: season,
          designation: labelRecette,
          typeOperation: "Recette",
        });

      if (
        exists &&
        !window.confirm(
          "Des lignes Pass’Sport existent déjà pour cette saison. Créer tout de même les écritures ?"
        )
      ) {
        return;
      }

      const { depensePlan, recettePlan } = findPassSportPlans(planComptables);

      await createDepenseRecetteClub({
        saison: season,
        date_operation: todayStr(),
        designation: labelDepense,
        categorie: "Pass'Sport",
        beneficiaire: "Adhérents",
        montant: Number(totalPassSport.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Dépense",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: depensePlan?.id ?? null,
      });

      await createDepenseRecetteClub({
        saison: season,
        date_operation: todayStr(),
        designation: labelRecette,
        categorie: "Pass'Sport",
        beneficiaire: "État",
        montant: Number(totalPassSport.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: recettePlan?.id ?? null,
      });

      await fetchAll();

      setFilter("saison", season);
      toast.success(
        `Écritures Pass’Sport créées (${totalPassSport.toFixed(2)} €).`
      );
    } catch (error) {
      console.error("[addPassSport] error:", error);
      toast.error("Impossible d’ajouter les écritures Pass’Sport.");
    } finally {
      setAutomation("passSport", false);
    }
  }, [
    automationLoading.passSport,
    fetchAll,
    getCurrentSeason,
    operationExists,
    planComptables,
    setAutomation,
    setFilter,
  ]);

  const addReductions = useCallback(async () => {
    if (automationLoading.reductions) return;
    setAutomation("reductions", true);

    try {
      const season = await getCurrentSeason();

      if (!season) {
        toast.error("Saison introuvable. Sélectionnez une saison.");
        return;
      }

      const regs = safeArray(await getAllReglementInscription());

      const totalReductions = regs
        .filter(
          (reglement) =>
            String(reglement?.saison || "") === season &&
            String(reglement?.statut_reglement || "") === "Réglé"
        )
        .reduce(
          (sum, reglement) =>
            sum +
            Math.abs(toNumber(reglement?.reduction_foyer)) +
            Math.abs(toNumber(reglement?.reduction_saison)),
          0
        );

      if (totalReductions <= 0) {
        toast.warn(`Aucune réduction trouvée pour la saison ${season}.`);
        return;
      }

      const label = `Réductions (famille + saison) accordées — ${season}`;

      if (
        operationExists({
          saison: season,
          designation: label,
          typeOperation: "Dépense",
        }) &&
        !window.confirm(`Une ligne "${label}" existe déjà. En créer une autre ?`)
      ) {
        return;
      }

      const plan = findReductionsPlan(planComptables);

      await createAutomaticOperation({
        saison: season,
        date_operation: todayStr(),
        designation: label,
        categorie: "Réductions",
        beneficiaire: "Adhérents",
        montant: Number(totalReductions.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Dépense",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: plan?.id ?? null,
      });

      setFilter("saison", season);
      toast.success(`Réductions ajoutées (${totalReductions.toFixed(2)} €).`);
    } catch (error) {
      console.error("[addReductions] error:", error);
      toast.error("Impossible d’ajouter la ligne de réductions.");
    } finally {
      setAutomation("reductions", false);
    }
  }, [
    automationLoading.reductions,
    createAutomaticOperation,
    getCurrentSeason,
    operationExists,
    planComptables,
    setAutomation,
    setFilter,
  ]);

  const addBoutiqueCA = useCallback(async () => {
    if (automationLoading.boutique) return;
    setAutomation("boutique", true);

    try {
      const season = await getCurrentSeason();

      if (!season) {
        toast.error("Saison introuvable. Sélectionnez une saison.");
        return;
      }

      const { start, end } = seasonBounds(season);
      const achats = safeArray(await getAchatsBoutique());

      const boutiqueRows = achats.filter((achat) => {
        if (!achat?.Article) return false;

        const date = new Date(achat?.date_achat);
        if (!(date >= start && date <= end)) return false;

        const name = achat?.Article?.nom || "";
        const desc = achat?.Article?.description || "";
        const message = achat?.message_achat || "";

        if (
          isAcompteText(name) ||
          isAcompteText(desc) ||
          isAcompteText(message)
        ) {
          return false;
        }

        return true;
      });

      const totalCA = boutiqueRows.reduce(
        (sum, achat) => sum + toNumber(achat?.prix_total),
        0
      );

      if (totalCA <= 0) {
        toast.warn(`Aucun chiffre d’affaires boutique pour la saison ${season}.`);
        return;
      }

      const label = `Chiffre d’affaires boutique — ${season}`;

      if (
        operationExists({
          saison: season,
          designation: label,
          typeOperation: "Recette",
        }) &&
        !window.confirm(`Une ligne "${label}" existe déjà. En créer une autre ?`)
      ) {
        return;
      }

      const plan = findBoutiquePlan(planComptables);

      await createAutomaticOperation({
        saison: season,
        date_operation: todayStr(),
        designation: label,
        categorie: "Boutique",
        beneficiaire: "Club",
        montant: Number(totalCA.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: plan?.id ?? null,
      });

      setFilter("saison", season);
      toast.success(
        `Chiffre d’affaires boutique ajouté (${totalCA.toFixed(2)} €).`
      );
    } catch (error) {
      console.error("[addBoutiqueCA] error:", error);
      toast.error("Impossible d’ajouter le chiffre d’affaires boutique.");
    } finally {
      setAutomation("boutique", false);
    }
  }, [
    automationLoading.boutique,
    createAutomaticOperation,
    getCurrentSeason,
    operationExists,
    planComptables,
    setAutomation,
    setFilter,
  ]);

  const deleteOperation = useCallback(async (operationId) => {
    if (!window.confirm("Supprimer cette opération ?")) return;

    try {
      await deleteDepenseRecetteClub(operationId);

      setData((prev) =>
        prev.filter((operation) => operation.id !== operationId)
      );

      toast.success("Opération supprimée.");
    } catch (error) {
      console.error("[deleteOperation] error:", error);
      toast.error("Erreur lors de la suppression de l’opération.");
    }
  }, []);

  const exportFiltered = useCallback(async () => {
    const rows = buildExportRows(filtered, planComptables);

    if (!rows.length) {
      toast.info("Aucune opération à exporter.");
      return;
    }

    try {
      await downloadRowsAsXlsx(rows);
      toast.success("Export XLSX généré.");
    } catch (error) {
      console.warn("XLSX indisponible, fallback CSV", error);
      downloadRowsAsCsv(rows);
      toast.info("Export CSV généré.");
    }
  }, [filtered, planComptables]);

  return {
    data,
    filtered,
    planComptables,
    settings,
    loading,

    filters,
    setFilter,
    resetFilters,

    saisons,
    categories,
    totals,
    stats,

    automationLoading,

    fetchAll,
    deleteOperation,
    exportFiltered,

    addCotisation,
    addLicences,
    addPassSport,
    addReductions,
    addBoutiqueCA,
  };
}